import React from "react";
import { Box, Portal } from "@chakra-ui/react";

const GAP = 8;
const VIEWPORT_PADDING = 8;

interface CustomTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ children, content }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const [coords, setCoords] = React.useState({
    top: -9999,
    left: -9999,
    visibility: "hidden" as "hidden" | "visible",
  });

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const calculatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();

    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;

    let top = triggerRect.top - tooltipHeight - GAP;

    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    if (left + tooltipWidth > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - tooltipWidth - VIEWPORT_PADDING;
    }

    if (top < VIEWPORT_PADDING) {
      top = triggerRect.bottom + GAP;
    }

    if (top + tooltipHeight > viewportHeight - VIEWPORT_PADDING) {
      top = viewportHeight - tooltipHeight - VIEWPORT_PADDING;
    }

    setCoords({
      top,
      left,
      visibility: "visible",
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      calculatePosition();
    });
  }, [isOpen, calculatePosition]);

  return (
    <>
      <Box
        ref={triggerRef}
        display="inline-flex"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        w={"fit-content"}
      >
        {children}
      </Box>

      {isOpen && (
        <Portal>
          <Box
            ref={tooltipRef}
            position="fixed"
            top={`${coords.top}px`}
            left={`${coords.left}px`}
            visibility={coords.visibility}
            w={"fit-content"}
            bg="white"
            borderRadius="8px"
            boxShadow="2xl"
            border="1px solid #E2E8F0"
            p="8px 10px"
            zIndex={999999}
            pointerEvents="none"
          >
            {content}
          </Box>
        </Portal>
      )}
    </>
  );
};

export default CustomTooltip;
